import React from "react";
import { Tag, MapPin, Package } from "lucide-react";
import { GraficaChip } from "@/components/PermisoCorreo";

const fmtFecha = (iso) => (iso ? new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "2-digit" }) : null);

function PermisoDetalle({ permiso, compacto, testId }) {
  if (!permiso) return null;
  return (
    <div className={`mb-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1.5 ${compacto ? "text-[11px]" : "text-xs"}`} data-testid={`${testId}-permiso`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-[10px] uppercase tracking-wider text-sky-200 font-semibold">📩 Permiso cliente</span>
        {(permiso.fecha_autorizada || permiso.hora_franja) && (
          <span className="font-bold text-sky-100" data-testid={`${testId}-permiso-franja`}>
            {permiso.fecha_autorizada ? fmtFecha(permiso.fecha_autorizada) : ""}{permiso.fecha_autorizada && permiso.hora_franja ? " · " : ""}{permiso.hora_franja ? `🕘 ${permiso.hora_franja}` : ""}
          </span>
        )}
        <GraficaChip cg={permiso} testId={`${testId}-permiso-grafica`} />
      </div>
      {permiso.observaciones && <div className="text-sky-100/90 mt-0.5" data-testid={`${testId}-permiso-obs`}>📝 {permiso.observaciones}</div>}
      {permiso.centro_autoriza && <div className="text-[10px] text-sky-200/70">Autoriza: {permiso.centro_autoriza}</div>}
    </div>
  );
}

/**
 * Detalle de montaje (común RutaCreate / RutaDetail / Mi Ruta).
 * Muestra ref. cliente, zona y una lista estructurada de piezas: descripción funcional
 * (p.ej. "Perfumes masculinos"), material, medidas, cantidad, código/ref y observaciones.
 * Sólo lectura: usa `detalle_montaje` calculado en backend desde documentos existentes.
 */
export default function DetalleMontaje({ detalle, campana, marca, compacto = false, max = 8, testId = "detalle-montaje" }) {
  if (!detalle) return null;
  const { piezas = [], comun = {}, referencia_cliente, zona, descripcion_linea, notas_linea, sin_componentes, permiso } = detalle;
  const txt = compacto ? "text-[11px]" : "text-xs";
  const visibles = piezas.slice(0, max);
  return (
    <div className={`rounded-lg border border-zinc-800/80 bg-zinc-950/40 p-2 ${txt}`} data-testid={testId}>
      {!compacto && <PermisoDetalle permiso={permiso} compacto={compacto} testId={testId} />}
      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
        <span className="text-[10px] uppercase tracking-wider text-amber-300/90 font-semibold inline-flex items-center gap-1"><Package className="w-3 h-3" /> Material a montar</span>
        {(campana || marca) && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-200 border border-violet-500/30 font-mono" data-testid={`${testId}-campana`}>{[campana, marca].filter(Boolean).join(" · ")}</span>}
        {referencia_cliente && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-200 border border-amber-500/30 font-mono" data-testid={`${testId}-ref`}>Ref. {referencia_cliente}</span>}
        {zona && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-200 border border-sky-500/30 inline-flex items-center gap-1" data-testid={`${testId}-zona`}><MapPin className="w-2.5 h-2.5" />{zona}</span>}
        {(comun.material || comun.medidas) && (
          <span className="text-[10px] text-zinc-400" data-testid={`${testId}-comun`}>Todas: {[comun.material, comun.medidas].filter(Boolean).join(" · ")}</span>
        )}
      </div>

      {sin_componentes ? (
        <div className="text-zinc-300" data-testid={`${testId}-fallback`}>
          {descripcion_linea || <span className="italic text-zinc-500">Sin componentes registrados · montaje según línea</span>}
        </div>
      ) : (
        <ul className="space-y-1" data-testid={`${testId}-piezas`}>
          {visibles.map((p, i) => <Pieza key={p.id || i} p={p} comun={comun} compacto={compacto} testId={testId} />)}
          {piezas.length > max && <li className="text-[10px] text-zinc-500 italic">+ {piezas.length - max} pieza(s) más…</li>}
        </ul>
      )}

      {!compacto && descripcion_linea && !sin_componentes && (
        <div className="mt-1.5 text-[11px] text-zinc-400" data-testid={`${testId}-descripcion-linea`}>{descripcion_linea}</div>
      )}
      {notas_linea && (
        <div className="mt-1 text-[11px] text-amber-200/90" data-testid={`${testId}-notas`}>📝 {notas_linea}</div>
      )}
    </div>
  );
}

function Pieza({ p, comun, compacto, testId }) {
  const titulo = p.descripcion || p.nombre;
  const secundario = p.descripcion && p.nombre && p.descripcion !== p.nombre ? p.nombre : null;
  const partes = [
    !comun.material && p.material,
    !comun.medidas && p.medidas,
    p.cantidad && p.cantidad !== 1 ? `${p.cantidad} ud` : (compacto ? null : "1 ud"),
    p.bulto_numero ? `Bulto #${p.bulto_numero}` : null,
  ].filter(Boolean);
  return (
    <li className="flex items-start gap-1.5" data-testid={`${testId}-pieza-${p.id}`}>
      <span className="text-zinc-600 mt-[3px]">•</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-1.5">
          <span className="font-semibold text-zinc-100" data-testid={`${testId}-pieza-titulo-${p.id}`}>{titulo}</span>
          {secundario && <span className="text-zinc-500">({secundario})</span>}
          <GraficaChip cg={p.cambio_grafica} testId={`${testId}-pieza-grafica-${p.id}`} />
          {partes.length > 0 && <span className="text-zinc-400">— {partes.join(" — ")}</span>}
          {p.referencia_cliente && <span className="font-mono text-[10px] px-1 rounded bg-amber-500/10 text-amber-200 border border-amber-500/30 inline-flex items-center gap-0.5"><Tag className="w-2.5 h-2.5" />{p.referencia_cliente}</span>}
        </div>
        {p.observaciones && <div className="text-[10px] text-zinc-400 italic truncate" title={p.observaciones}>{p.observaciones}</div>}
        {p.subpiezas?.length > 0 && (
          <ul className="ml-2 mt-0.5 space-y-0.5 text-[10px] text-zinc-400">
            {p.subpiezas.map((s) => <li key={s.id}>↳ {s.descripcion || s.nombre}{s.medidas ? ` · ${s.medidas}` : ""}{s.cantidad && s.cantidad !== 1 ? ` · ${s.cantidad} ud` : ""}</li>)}
          </ul>
        )}
      </div>
    </li>
  );
}
